import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import handleFileExport from '@vendys-frontend/excel-export';
import { useNavigate } from 'react-router-dom';
import { Button, Row } from 'antd';

import { DateNavigation } from 'components';
import { useMealPlanCategories, useMealPlanListPerCategory, useUpdateMealPlanSort } from 'hooks';

import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import MealPlanCard from './components/MealPlanCard';
import MealPlanExcelUploadModal from './components/MealPlanExcelUploadModal';
import MealPlanExcelLayout from './excelLayout/MealPlanExcelLayout.json';
import { ContentWrapper, CategoryWrapper, StyledTabs } from './styles';

const MealPlanListPage = () => {
  const navigate = useNavigate();
  const { data: categories } = useMealPlanCategories();
  const { data: mealPlanList, mutateAsync: getMealPlanListPerCategory } = useMealPlanListPerCategory();
  const { mutateAsync: updateMealPlanSort } = useUpdateMealPlanSort();

  const [activeCategoryId, setActiveCategoryId] = useState<string>();
  const [selectedTab, setSelectedTab] = useState<string>('ALL');
  const [serveDate, setServeDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [isModalOnOff, setIsModalOnOff] = useState<boolean>(false);

  const [localMealPlanList, setLocalMealPlanList] = useState(mealPlanList);

  useEffect(() => {
    getMealPlanListPerCategory(serveDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serveDate]);

  useEffect(() => {
    const array = mealPlanList?.filter(({ categoryId }) => categoryId === selectedTab);
    setLocalMealPlanList(selectedTab !== 'ALL' ? array : mealPlanList);
  }, [mealPlanList, selectedTab]);

  useEffect(() => {
    window.addEventListener('dragover', (event) => event.preventDefault());
    window.addEventListener('drop', (event) => event.preventDefault());
    return () => {
      window.addEventListener('dragover', (event) => event.preventDefault());
      window.addEventListener('drop', (event) => event.preventDefault());
    };
  }, []);

  const handleTabChange = (tabKey: string) => setSelectedTab(tabKey);

  const handleDateChange = (date: Date) => setServeDate(moment(date).format('YYYY-MM-DD'));

  const handleExcelDownload = () => {
    const fileName = `식단표_${moment().valueOf()}`;
    const newRows = [];
    localMealPlanList.forEach(({ mealPlans }) => {
      mealPlans.forEach((m) => {
        newRows.push([
          m.serveDate,
          m.categoryName,
          m.mealPlanName,
          m.content,
          m.mealPlanItems.length,
          m.totalCalories,
          m.totalNatrium,
          m.totalSugar,
          m.totalCarbohydrate,
          m.totalProtein,
          m.totalFat
        ]);
      });
    });
    const apiExcelDatas = { 0: { startRowNum: 2, newRows } };
    handleFileExport(MealPlanExcelLayout, apiExcelDatas, fileName);
  };

  const handleAddMenuPlan = () => navigate('/mealPlan/add');

  const mealPlanCategories = useMemo(() => {
    const defaultCategory = [{ categoryId: 'ALL', categoryName: '전체' }];
    return defaultCategory
      .concat(categories || [])
      .map(({ categoryId, categoryName }) => ({ key: categoryId, label: categoryName }));
  }, [categories]);

  const sensors = useSensors(useSensor(MouseSensor));

  const handleDragStart = (categoryId: string) => setActiveCategoryId(categoryId);
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      setLocalMealPlanList((items) => {
        const index = items.findIndex((item) => item.categoryId === activeCategoryId);
        const category = items[index];

        const tartget = category.mealPlans.find((item) => item.mealPlanIdx === active.id);
        const oldIndex = category.mealPlans.findIndex((item) => item.mealPlanIdx === active.id);
        const newIndex = category.mealPlans.findIndex((item) => item.mealPlanIdx === over.id);
        const mealPlans = arrayMove(category.mealPlans, oldIndex, newIndex);

        updateMealPlanSort({ mealPlanIdx: tartget.mealPlanIdx, params: { sortBy: newIndex + 1 } });

        return items.map((item, idx) => (idx === index ? { ...item, mealPlans } : item));
      });
    }
  };

  const handleOnOffExcelUploadModal = () => setIsModalOnOff(!isModalOnOff);
  const handleConfirm = () => {
    handleOnOffExcelUploadModal();
    getMealPlanListPerCategory(serveDate);
  };

  return (
    <div>
      <DateNavigation infinite date={new Date(moment(serveDate).valueOf())} onChange={handleDateChange} />
      <ContentWrapper>
        <StyledTabs items={mealPlanCategories} onChange={handleTabChange} />
        <div className="button-group">
          <Button type="primary" onClick={handleOnOffExcelUploadModal}>
            엑셀 일괄 업로드
          </Button>
          <Button type="primary" onClick={handleExcelDownload}>
            엑셀 다운로드
          </Button>
          <Button onClick={handleAddMenuPlan}>+ 새 식단표 등록</Button>
        </div>
        <div>
          {localMealPlanList?.length ? (
            localMealPlanList.map(({ categoryId, categoryName, mealPlans }) => (
              <CategoryWrapper key={categoryId}>
                <DndContext
                  sensors={sensors}
                  modifiers={[restrictToParentElement]}
                  onDragStart={() => handleDragStart(categoryId)}
                  onDragEnd={handleDragEnd}
                >
                  {selectedTab === 'ALL' ? (
                    <div className="caregory-name">
                      <span>{categoryName}</span>
                      <span className="menu-count">({mealPlans?.length}개)</span>
                    </div>
                  ) : null}
                  <Row gutter={[16, 16]} style={{ padding: '16px 0px' }}>
                    <SortableContext items={mealPlans.map(({ mealPlanIdx }) => mealPlanIdx)} strategy={rectSortingStrategy}>
                      {mealPlans?.map((m) => (
                        <MealPlanCard
                          key={m.mealPlanIdx}
                          mealPlanIdx={m.mealPlanIdx}
                          mealPlanName={m.mealPlanName}
                          mealPlanImage={m.imageUrl}
                          mealPlanItems={m.mealPlanItems}
                          mealPlanContent={m.content}
                          nutritionInfo={{
                            totalCalories: m.totalCalories,
                            totalCarbohydrate: m.totalCarbohydrate,
                            totalProtein: m.totalProtein,
                            totalFat: m.totalFat,
                            totalNatrium: m.totalNatrium,
                            totalSugar: m.totalSugar
                          }}
                        />
                      ))}
                    </SortableContext>
                  </Row>
                </DndContext>
              </CategoryWrapper>
            ))
          ) : (
            <div style={{ textAlign: 'center', paddingTop: 54 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#999' }}>등록된 식단이 없습니다</div>
              <div style={{ fontSize: 16, fontWeight: 400, color: '#999' }}>식단표 미등록 시 사용자에게 노출 되지 않습니다</div>
            </div>
          )}
        </div>
      </ContentWrapper>
      <MealPlanExcelUploadModal
        open={isModalOnOff}
        categories={categories}
        onCancel={handleOnOffExcelUploadModal}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default MealPlanListPage;
